/**
 * Schedule board model: pure helpers shared by the day timeline, the week grid and the map.
 *
 * A job's appointment is scheduled_date (midnight in the company timezone, timestamptz) plus
 * scheduled_time_start / scheduled_time_end ("HH:mm" text, the arrival window). There is no
 * duration column: a job without an end is drawn as DEFAULT_WINDOW_MIN long, and a job without
 * a start is an "anytime" job for that day.
 */
import { addDays, format, parseISO } from 'date-fns';
import { formatInTz } from '@/utils/timezone';
import type { TechProgressSource } from '@/utils/techProgress';

export interface ScheduleJob extends TechProgressSource {
  id: string;
  job_number: string | null;
  status: string;
  scheduled_date: string | null;
  scheduled_time: string | null;
  scheduled_time_start: string | null;
  scheduled_time_end: string | null;
  assigned_tech_id: string | null;
  customer_name: string | null;
  customer_address: string | null;
  customer_suburb: string | null;
  customer_postcode: string | null;
  issue_description: string | null;
  lat: number | null;
  lng: number | null;
  metro_id: string | null;
  service_types: { name: string } | null;
  techs: { id: string; name: string } | null;
}

/** A lane on the board: a technician, or the unassigned tray (id null). */
export interface ScheduleLane {
  key: string;            // tech id, or UNASSIGNED
  techId: string | null;
  name: string;
  colour: string;         // CSS colour from the --tech-N tokens
  inactive?: boolean;     // deactivated tech who still holds jobs in range
}

export const UNASSIGNED = 'unassigned';
export const DEFAULT_WINDOW_MIN = 120;
export const SNAP_MIN = 30;
export const TECH_COLOUR_COUNT = 8;

/** Lane colour from the design tokens, stable for a given position in the tech list. */
export function techColour(index: number): string {
  return `hsl(var(--tech-${(index % TECH_COLOUR_COUNT) + 1}))`;
}

/** "09:30" → 570. Null for anything that is not a valid HH:mm. */
export function toMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** 570 → "09:30". */
export function fromMinutes(total: number): string {
  const clamped = Math.max(0, Math.min(23 * 60 + 59, Math.round(total)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** The job's window in minutes from midnight, or null for an anytime job. */
export function jobWindow(job: Pick<ScheduleJob, 'scheduled_time_start' | 'scheduled_time_end' | 'scheduled_time'>):
  { start: number; end: number; hasEnd: boolean } | null {
  const start = toMinutes(job.scheduled_time_start) ?? toMinutes(job.scheduled_time);
  if (start === null) return null;
  const rawEnd = toMinutes(job.scheduled_time_end);
  const hasEnd = rawEnd !== null && rawEnd > start;
  const end = hasEnd ? (rawEnd as number) : Math.min(start + DEFAULT_WINDOW_MIN, 24 * 60);
  return { start, end, hasEnd };
}

/** "09:00–11:00", "09:00", or "Anytime". */
export function windowLabel(job: Pick<ScheduleJob, 'scheduled_time_start' | 'scheduled_time_end' | 'scheduled_time'>): string {
  const s = toMinutes(job.scheduled_time_start) ?? toMinutes(job.scheduled_time);
  if (s === null) return 'Anytime';
  const e = toMinutes(job.scheduled_time_end);
  return e !== null && e > s ? `${fromMinutes(s)}–${fromMinutes(e)}` : fromMinutes(s);
}

/** The company-timezone calendar day ("yyyy-MM-dd") a job is booked for. */
export function jobDayKey(job: Pick<ScheduleJob, 'scheduled_date'>, timezone: string): string | null {
  if (!job.scheduled_date) return null;
  return formatInTz(job.scheduled_date, timezone, 'yyyy-MM-dd');
}

/** Calendar maths on plain "yyyy-MM-dd" keys (no clock, no timezone). */
export function shiftDay(day: string, days: number): string {
  return format(addDays(parseISO(day), days), 'yyyy-MM-dd');
}

export function weekStart(day: string, weekStartsOn: 0 | 1): string {
  const d = parseISO(day);
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  return format(addDays(d, -diff), 'yyyy-MM-dd');
}

export function weekDays(start: string): string[] {
  return Array.from({ length: 7 }, (_, i) => shiftDay(start, i));
}

/**
 * A job the board may move: not closed, and the technician is not on site yet. A job parked at
 * quote_sent (tech quoted and left) moves too: that is booking the return visit.
 */
export function isMovable(job: Pick<ScheduleJob, 'status' | 'arrived_at'>): boolean {
  if (job.status === 'quote_sent') return true;
  return job.status !== 'completed' && job.status !== 'cancelled' && !job.arrived_at;
}

/**
 * Stacks overlapping jobs of one lane into rows (greedy interval packing), so two jobs at
 * 9:00 sit one above the other instead of on top of each other.
 */
export function packRows<T extends { start: number; end: number }>(items: T[]): { item: T; row: number }[] {
  const sorted = [...items].sort((a, b) => a.start - b.start || b.end - a.end);
  const rowEnds: number[] = [];
  return sorted.map((item) => {
    let row = rowEnds.findIndex((end) => end <= item.start);
    if (row === -1) {
      row = rowEnds.length;
      rowEnds.push(item.end);
    } else {
      rowEnds[row] = item.end;
    }
    return { item, row };
  });
}

/** Visible hours for the day view: 7am–7pm, widened to fit any job outside it. */
export function visibleHours(jobs: ScheduleJob[]): { from: number; to: number } {
  let from = 7;
  let to = 19;
  for (const job of jobs) {
    const w = jobWindow(job);
    if (!w) continue;
    from = Math.min(from, Math.floor(w.start / 60));
    to = Math.max(to, Math.ceil(w.end / 60));
  }
  return { from: Math.max(0, from), to: Math.min(24, to) };
}

/** Where a drop lands. Encoded into dnd-kit droppable ids. */
export type DropTarget =
  | { view: 'day'; laneKey: string; zone: 'timeline' | 'anytime' }
  | { view: 'week'; laneKey: string; day: string };

export function dropId(target: DropTarget): string {
  return target.view === 'day'
    ? `day|${target.laneKey}|${target.zone}`
    : `week|${target.laneKey}|${target.day}`;
}

export function parseDropId(id: string): DropTarget | null {
  const [view, laneKey, rest] = id.split('|');
  if (!laneKey || !rest) return null;
  if (view === 'day' && (rest === 'timeline' || rest === 'anytime')) return { view, laneKey, zone: rest };
  if (view === 'week') return { view, laneKey, day: rest };
  return null;
}

/** The move a dispatcher asked for, before they confirm it. */
export interface ScheduleMove {
  job: ScheduleJob;
  day: string;
  start: string | null;
  end: string | null;
  techId: string | null;
}

export function moveChangesAnything(move: ScheduleMove, timezone: string): boolean {
  const { job } = move;
  if (job.status === 'quote_sent') return true;   // booking the return visit
  return (
    jobDayKey(job, timezone) !== move.day ||
    (job.scheduled_time_start ?? null) !== move.start ||
    (job.scheduled_time_end ?? null) !== move.end ||
    (job.assigned_tech_id ?? null) !== move.techId
  );
}
